/* Tabbed Shop All listing behaviour.

   Every card is rendered server side, so tabs, sorting and View More operate on the
   DOM rather than fetching. That is deliberate: Liquid caps collection.products at 50
   per pass and the theme's load-more pager appends results flat, both of which break
   grouping. Keeping the whole listing in the DOM also leaves it crawlable regardless
   of which tab is active. */

(function () {
  'use strict';

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  /* How many columns the grid is actually drawing, read back from the resolved
     grid-template-columns rather than assumed. The listing steps 2 → 3 → 4 across the breakpoints
     in assets/shop-all.css, so a hard-coded number would be wrong at two widths out of three. */
  function gridColumns(el) {
    if (!el || typeof window.getComputedStyle !== 'function') return 1;
    var value = window.getComputedStyle(el).gridTemplateColumns;
    if (!value || value === 'none') return 1;
    return value.split(' ').filter(Boolean).length || 1;
  }

  /* Round a reveal count up to a whole number of rows, so the last row of the grid is never left
     part empty (QA round 3, BugHerd 9). Capped at what actually exists, which is what
     stops it inventing cards when the listing is nearly exhausted — the final row is allowed to
     be short, because there is nothing left to fill it with. */
  function fillRow(count, columns, available) {
    if (columns < 2 || count >= available) return Math.min(count, available);
    return Math.min(available, Math.ceil(count / columns) * columns);
  }

  function ShopAll(root) {
    this.root = root;
    this.titleEl = root.querySelector('[data-shop-all-title]');
    this.countEl = root.querySelector('[data-shop-all-count]');
    this.sortEl = root.querySelector('[data-shop-all-sort]');
    this.sortValueEl = root.querySelector('[data-shop-all-sort-value]');
    this.allMoreEl = root.querySelector('[data-shop-all-more-all]');
    this.groupsEl = root.querySelector('[data-shop-all-groups]');
    this.tabButtons = toArray(root.querySelectorAll('[data-shop-all-tab]'));

    this.groups = toArray(root.querySelectorAll('[data-shop-all-group]')).map(function (el) {
      var cards = toArray(el.querySelectorAll('[data-shop-all-card]'));

      cards.forEach(function (card, index) {
        card.dataset.originalIndex = String(index);
      });

      var revealed = cards.filter(function (card) {
        return !card.classList.contains('shop-all-card--collapsed');
      }).length;

      return {
        el: el,
        tab: el.getAttribute('data-tab'),
        grid: el.querySelector('[data-shop-all-grid]'),
        moreEl: el.querySelector('[data-shop-all-group-more]'),
        cards: cards,
        order: cards.slice(),
        initial: revealed || cards.length,
        revealed: revealed || cards.length
      };
    });

    /* originalIndex is the card's position within its own group, which is all the grouped
       tabs need. The All tab sorts across group boundaries, so it needs a document-wide
       index to break price ties deterministically. */
    this.allCards = [];
    this.groups.forEach(function (group) {
      group.cards.forEach(function (card) {
        card.dataset.globalIndex = String(this.allCards.length);
        this.allCards.push(card);
      }, this);
    }, this);

    this.allOrder = this.allCards.slice();

    /* Tabs are section blocks, so the valid keys come from the rendered buttons rather than a
       fixed list. A page with no tab blocks renders no buttons at all: every group stays
       visible, there is no tab bar, and the single View More applies. */
    this.validTabs = this.tabButtons.map(function (button) {
      return button.getAttribute('data-shop-all-tab');
    });
    this.untabbed = this.validTabs.length === 0;
    this.defaultTab = root.getAttribute('data-active-tab') || this.validTabs[0] || 'all';

    this.tab = this.defaultTab;
    this.sort = 'featured';

    this.bind();
    this.readUrl();
    this.render();
    this.initAddToCart();
  }

  ShopAll.prototype.bind = function () {
    var self = this;

    this.tabButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        self.setTab(button.getAttribute('data-shop-all-tab'), true);
      });
    });

    if (this.sortEl) {
      this.sortEl.addEventListener('change', function () {
        self.sort = self.sortEl.value;
        self.syncSortValue();
        self.render();
        self.writeUrl();
      });
    }

    this.groups.forEach(function (group) {
      if (!group.moreEl) return;

      group.moreEl.addEventListener('click', function (event) {
        if (!event.target.closest('[data-shop-all-more]')) return;
        group.revealed = Math.min(group.revealed + group.initial, group.cards.length);
        self.applyReveal();
      });
    });

    /* The All tab's button grows every group by its own step rather than tracking a separate
       total. That keeps the two tabs in step — revealing more on All also reveals more on
       Devices — and it is what lets the All tab reveal a spread across groups instead of
       emptying the first one before touching the second. */
    if (this.allMoreEl) {
      this.allMoreEl.addEventListener('click', function (event) {
        if (!event.target.closest('[data-shop-all-more]')) return;

        self.groups.forEach(function (group) {
          group.revealed = Math.min(group.revealed + group.initial, group.cards.length);
        });

        self.applyReveal();
      });
    }

    window.addEventListener('popstate', function () {
      self.readUrl();
      self.render();
    });

    /* The grid steps 2 → 3 → 4 columns at 768, 991 and 1280, so a width change can leave the last
       row part empty again even though nothing was clicked. Re-running the reveal recomputes the
       rounding for the new column count.

       Gated on the count actually changing, and both grids are in the signature because only one
       of them is a grid at a time: .shop-all__groups on the All tab, the per-group grid otherwise.
       Without that gate this would fire on every scroll-driven toolbar resize on mobile. */
    var columnSignature = function () {
      var first = self.groups[0];
      return gridColumns(self.groupsEl) + ':' + (first ? gridColumns(first.grid) : 0);
    };

    var lastSignature = columnSignature();
    var resizeTimer = null;

    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var signature = columnSignature();
        if (signature === lastSignature) return;
        lastSignature = signature;
        self.applyReveal();
      }, 150);
    });
  };

  ShopAll.prototype.totalCards = function () {
    return this.groups.reduce(function (total, group) {
      return total + group.cards.length;
    }, 0);
  };

  ShopAll.prototype.revealedTotal = function () {
    return this.groups.reduce(function (total, group) {
      return total + group.revealed;
    }, 0);
  };

  ShopAll.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var tab = params.get('tab');
    var sort = params.get('sort');

    this.tab = this.validTabs.indexOf(tab) > -1 ? tab : this.defaultTab;

    if (sort === 'price-asc' || sort === 'price-desc' || sort === 'featured') {
      this.sort = sort;
    }

    if (this.sortEl) this.sortEl.value = this.sort;
    this.syncSortValue();
  };

  /* The visible value is markup, not the select's own text, so it has to be kept in step. */
  ShopAll.prototype.syncSortValue = function () {
    if (!this.sortEl || !this.sortValueEl) return;

    var selected = this.sortEl.options[this.sortEl.selectedIndex];
    if (selected) this.sortValueEl.textContent = selected.textContent.trim();
  };

  ShopAll.prototype.writeUrl = function () {
    if (!window.history || !window.history.replaceState) return;

    var params = new URLSearchParams(window.location.search);

    if (this.tab === this.defaultTab) {
      params.delete('tab');
    } else {
      params.set('tab', this.tab);
    }

    if (this.sort === 'featured') {
      params.delete('sort');
    } else {
      params.set('sort', this.sort);
    }

    var query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : ''));
  };

  ShopAll.prototype.setTab = function (tab, pushUrl) {
    if (this.validTabs.indexOf(tab) === -1) return;

    this.tab = tab;
    this.render();

    if (pushUrl) this.writeUrl();
  };

  ShopAll.prototype.render = function () {
    this.root.setAttribute('data-active-tab', this.tab);

    this.tabButtons.forEach(function (button) {
      var isActive = button.getAttribute('data-shop-all-tab') === this.tab;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }, this);

    if (this.titleEl) {
      var label = this.titleEl.getAttribute('data-label-' + this.tab);
      if (label) this.titleEl.textContent = label;
    }

    this.groups.forEach(function (group) {
      var visible = this.tab === 'all' || group.tab === this.tab;
      group.el.hidden = !visible;
    }, this);

    this.applySort();
    this.applyReveal();
    this.updateCount();
  };

  /* "Featured" means each group's curated order, driven by the rank metafield the section
     names rather than a hardcoded list. Lowest rank first, so on Shop All the plan scheme
     10 Unlimited / 20 GoData / 30 Pay-Per-GB / 40 Daypass puts Unlimited at the top of each
     coverage group. Cards with no rank keep their collection order at the end of the group,
     and a group where nothing is ranked is left in collection order untouched. */
  ShopAll.prototype.applySort = function () {
    var sort = this.sort;

    this.groups.forEach(function (group) {
      var order = group.cards.slice();

      if (sort === 'price-asc' || sort === 'price-desc') {
        var direction = sort === 'price-asc' ? 1 : -1;

        order.sort(function (a, b) {
          var diff = (parseInt(a.dataset.price, 10) || 0) - (parseInt(b.dataset.price, 10) || 0);
          if (diff !== 0) return diff * direction;
          return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });
      } else {
        var hasSize = group.cards.some(function (card) {
          return card.dataset.size !== undefined && card.dataset.size !== '';
        });

        if (hasSize) {
          order.sort(function (a, b) {
            var aHas = a.dataset.size !== undefined && a.dataset.size !== '';
            var bHas = b.dataset.size !== undefined && b.dataset.size !== '';

            if (aHas !== bHas) return aHas ? -1 : 1;

            /* Lowest rank shows first, so a rank of 10 beats 20. Products without a rank are
               already pushed to the end by the aHas check above, whichever way this compares. */
            if (aHas && bHas) {
              var diff = (parseInt(a.dataset.size, 10) || 0) - (parseInt(b.dataset.size, 10) || 0);
              if (diff !== 0) return diff;
            }

            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
          });
        }
      }

      group.order = order;

      if (!group.grid) return;

      var fragment = document.createDocumentFragment();
      order.forEach(function (card) {
        fragment.appendChild(card);
      });
      group.grid.appendChild(fragment);
    });

    this.applyAllOrder();
  };

  /* The All tab merges every group into one visual grid via display: contents, but the cards
     stay in their own group grids in the DOM, so a price sort applied per group comes out as
     a sawtooth: 5G ascending, then 4G ascending, then Accessories ascending. Shoppers read
     that as broken (Deni Lamani, QA round 2).

     Sorting across groups is therefore done with the CSS `order` property rather than by
     moving cards between grids. display: contents promotes the cards to grid items of
     .shop-all__groups, so `order` reorders them, and clearing it restores the grouped tabs
     without touching the DOM. Featured keeps DOM order, which is the curated grouping. */
  ShopAll.prototype.applyAllOrder = function () {
    var order = [];

    this.groups.forEach(function (group) {
      order = order.concat(group.order);
    });

    var isPriceSort = this.sort === 'price-asc' || this.sort === 'price-desc';

    if (this.tab === 'all' && isPriceSort) {
      var direction = this.sort === 'price-asc' ? 1 : -1;

      order.sort(function (a, b) {
        var diff = (parseInt(a.dataset.price, 10) || 0) - (parseInt(b.dataset.price, 10) || 0);
        if (diff !== 0) return diff * direction;
        return Number(a.dataset.globalIndex) - Number(b.dataset.globalIndex);
      });

      order.forEach(function (card, index) {
        card.style.order = String(index);
      });
    } else {
      order.forEach(function (card) {
        card.style.order = '';
      });
    }

    this.allOrder = order;
  };

  /* Two reveal modes, because the two sorts want different things from the All tab.

     Under a price sort the whole tab is one flat list, so it reveals the cheapest N outright.
     Under the default sort the grouping is the point, so each group reveals its own share, which
     is also what the server rendered. The previous version always spent one shared budget in
     group order, which meant the first group took everything it could hold and later groups
     could reveal nothing at all until View More was pressed. */
  ShopAll.prototype.applyReveal = function () {
    var isAll = this.tab === 'all';
    var isPriceSort = this.sort === 'price-asc' || this.sort === 'price-desc';

    /* What each mode counts as "the grid" differs, and that decides what has to be rounded.
       On the All tab the groups collapse to display: contents and every card becomes an item of
       .shop-all__groups, so there is one grid and the number that matters is the running total.
       On a grouped tab each group keeps its own grid, so each is rounded on its own. */
    var shownTotal;

    if (isAll) {
      var allColumns = gridColumns(this.groupsEl);
      shownTotal = fillRow(this.revealedTotal(), allColumns, this.totalCards());
    }

    if (isAll && isPriceSort) {
      var limit = Math.max(0, shownTotal);

      this.allOrder.forEach(function (card, index) {
        card.classList.toggle('shop-all-card--collapsed', index >= limit);
      });

      this.groups.forEach(function (group) {
        if (group.moreEl) group.moreEl.hidden = true;
      });
    } else {
      /* Under the default sort the All tab still reveals per group, so the rounding has to be
         handed out rather than applied once: the groups keep their own shares and whatever the
         round-up added goes to the first groups that still have something hidden. */
      var spare = isAll ? shownTotal - this.revealedTotal() : 0;

      this.groups.forEach(function (group) {
        var inTab = isAll || group.tab === this.tab;
        var groupLimit;

        if (!inTab) {
          groupLimit = 0;
        } else if (isAll) {
          groupLimit = group.revealed;
          if (spare > 0) {
            var give = Math.min(group.cards.length - groupLimit, spare);
            groupLimit += give;
            spare -= give;
          }
        } else {
          groupLimit = fillRow(group.revealed, gridColumns(group.grid), group.order.length);
        }

        group.order.forEach(function (card, index) {
          card.classList.toggle('shop-all-card--collapsed', index >= groupLimit);
        });

        if (group.moreEl) {
          group.moreEl.hidden = isAll || groupLimit >= group.order.length;
        }
      }, this);
    }

    /* Compared against what is on screen, not against `revealed`. Rounding up can finish the
       listing early, and leaving the button showing with nothing left to reveal would make it
       look broken. */
    if (this.allMoreEl) {
      this.allMoreEl.hidden = !isAll || shownTotal >= this.totalCards();
    }
  };

  /* Distinct product ids in the active tab. Products can legitimately sit in two
     coverage collections, so a plain card count would double count them. */
  ShopAll.prototype.updateCount = function () {
    if (!this.countEl) return;

    if (this.tab === 'all') {
      var allLabel = this.countEl.getAttribute('data-label-all');
      if (allLabel) this.countEl.textContent = allLabel;
      return;
    }

    var ids = {};
    var total = 0;

    this.groups.forEach(function (group) {
      if (group.tab !== this.tab) return;

      group.cards.forEach(function (card) {
        var id = card.dataset.productId;
        if (id && !ids[id]) {
          ids[id] = true;
          total += 1;
        }
      });
    }, this);

    var template = this.countEl.getAttribute('data-label-filtered') || '[count]';
    this.countEl.textContent = template.replace('[count]', String(total));
  };

  /* Reuse the theme's own ajax add handler for single-variant cards rather than
     writing a second cart path. Same wiring as sections/product-carousel.liquid.

     Bound on DOMContentLoaded, not at construction. theme.js is what defines
     applyAjaxToProductForm, and layout/theme.liquid loads it deferred at the very end of the
     body, while this file is loaded deferred by the section from inside <main>. Deferred scripts
     run in document order, so this one executes first — and it executes while readyState is
     already "interactive", so the constructor runs immediately rather than waiting.

     window.theme is truthy by then, because an inline script near the top of the layout sets
     theme.strings, but applyAjaxToProductForm is not attached yet. The guard below therefore
     returned silently on every page load and the button was never wired at all: no "Adding"
     state, no tick, just a plain form post (QA round 3, BugHerd 7). The PDP was
     unaffected because it wires itself up from inside theme.js.

     DOMContentLoaded is the fix rather than a timer: it is specified to fire only after every
     deferred script has executed, so theme.js is guaranteed to be ready. */
  ShopAll.prototype.initAddToCart = function () {
    var root = this.root;

    function bindForms() {
      if (typeof window.jQuery !== 'function') return;
      if (!window.theme || typeof window.theme.applyAjaxToProductForm !== 'function') return;

      toArray(root.querySelectorAll('[data-ajax-add-to-cart="true"]')).forEach(function (wrapper) {
        window.theme.applyAjaxToProductForm(window.jQuery(wrapper));
      });
    }

    /* "complete" covers a section re-render in the theme editor, where theme.js loaded long ago
       and there is no further DOMContentLoaded to wait for. */
    if (document.readyState === 'complete') {
      bindForms();
    } else {
      document.addEventListener('DOMContentLoaded', bindForms);
    }
  };

  function init() {
    toArray(document.querySelectorAll('[data-shop-all]')).forEach(function (root) {
      if (root.dataset.shopAllReady === 'true') return;
      root.dataset.shopAllReady = 'true';
      new ShopAll(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
